import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { SelectVfoCase } from '@/entities/vfo/components/SelectVfoCase';
import { SelectVfoStatus } from '@/entities/vfo/components/SelectVfoStatus';
import { SelectVfoTag } from '@/entities/vfo/components/SelectVfoTag';
import { MembersFilters } from '@/entities/vfo/types';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { Form } from '@/features/vfo/uikit/Form';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { MembersFiltersModalProps } from '@/widgets/modals/types';

import { VfoModalContainerV5 } from '../containers/VfoModalContainerV5';

export const MembersFiltersModal: FC<MembersFiltersModalProps> = ({
  onApply,
  casesFilter,
  statusesFilter,
  tagsFilter,
  withCases,
}) => {
  const { t: tc } = useTranslation('common');
  const { t: tw } = useTranslation('widgets/modals', { keyPrefix: 'membersFilter' });
  const { t: tf } = useTranslation('pages/pageVfoMembers', { keyPrefix: 'filters' });
  const modals = useModalsStore();

  const { control, handleSubmit: formHandleSubmit } = useForm<MembersFilters>({
    defaultValues: {
      statusesFilter,
      tagsFilter,
      casesFilter,
    },
  });

  const { currentUser } = useMembers();

  const onSubmit = (data: MembersFilters) => {
    onApply(data);
    modals.closeModal();
  };

  return (
    <VfoModalContainerV5 title={tw('title')}>
      <StyledForm onFinish={formHandleSubmit(onSubmit)}>
        <FormItems>
          <Controller
            control={control}
            name="statusesFilter"
            render={({ field }) => (
              <SelectVfoStatus
                {...field}
                mode="multiple"
                placeholder={tf('statuses')}
                withInvited={currentUser?.isFounder}
              />
            )}
          />
          <Controller
            control={control}
            name="tagsFilter"
            render={({ field }) => (
              <SelectVfoTag {...field} mode="multiple" placeholder={tf('tags')} />
            )}
          />
          {withCases && (
            <Controller
              control={control}
              name="casesFilter"
              render={({ field }) => (
                <SelectVfoCase {...field} mode="multiple" placeholder={tf('cases')} />
              )}
            />
          )}
        </FormItems>
        <Buttons>
          <StyledButton type="default" size="middle" onClick={modals.closeModal}>
            {tc('cancel')}
          </StyledButton>
          <StyledButton type="primary" size="middle" htmlType="submit">
            {tc('apply')}
          </StyledButton>
        </Buttons>
      </StyledForm>
    </VfoModalContainerV5>
  );
};

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Buttons = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  align-items: center;
`;

const StyledButton = styled(Button)`
  flex: 1;
`;
